import React, { useState, useEffect } from 'react';
import AdminService from '../services/adminService';
import { FaSync, FaArrowUp, FaArrowDown, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const FleetComparison = () => {
  const [view, setView] = useState('head-to-head'); // 'head-to-head' | 'period-grid'
  const [mode, setMode] = useState('riders'); // 'riders' | 'merchants'

  // Date Range state
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const getNDaysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
  };

  const getMondayOf = (dateInput) => {
    const d = new Date(dateInput);
    const day = d.getDay(); // 0: Sun, 1: Mon, ... 6: Sat
    const diff = (day + 6) % 7; // 0 for Mon, 1 for Tue, ..., 6 for Sun
    const monday = new Date(d);
    monday.setDate(d.getDate() - diff);
    return monday;
  };

  const getFirstDayOfCurrentMonthStr = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  };

  const [dateFrom, setDateFrom] = useState(getFirstDayOfCurrentMonthStr());
  const [dateTo, setDateTo] = useState(getTodayStr());
  const [period, setPeriod] = useState('week'); // 'day' | 'week' | 'month'

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    const today = getTodayStr();
    if (newPeriod === 'day') {
      setDateFrom(getNDaysAgoStr(6));
      setDateTo(today);
    } else if (newPeriod === 'week') {
      setDateFrom(getFirstDayOfCurrentMonthStr());
      setDateTo(today);
    } else if (newPeriod === 'month') {
      setDateFrom(getNDaysAgoStr(179));
      setDateTo(today);
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);

  const [entityAId, setEntityAId] = useState('');
  const [entityBId, setEntityBId] = useState('');

  // Fetch comparison data when mode, date range or period changes
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const activeGranularity = period === 'custom' ? 'day' : period;
      const params = {
        mode,
        granularity: activeGranularity,
        period: activeGranularity
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await AdminService.getFleetComparison(params);
      if (res && res.success) {
        setComparisonData(res);
      } else {
        setError('Failed to load fleet comparison data');
      }
    } catch (err) {
      console.error('Error fetching fleet comparison:', err);
      setError(err.message || 'Error fetching comparison data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [mode, dateFrom, dateTo, period]);

  const entities = comparisonData?.entities || [];
  const rawBuckets = comparisonData?.buckets || [];
  const movers = comparisonData?.movers || [];

  const formatWeeklyBucketLabel = (b, idx) => {
    const raw = String(b?.label || b?.key || '').trim();
    if (!raw) return `W${idx + 1}`;

    if (raw.toUpperCase().startsWith('W/O') || raw.toUpperCase().startsWith('W')) {
      return raw.toUpperCase();
    }

    const refDate = dateFrom ? new Date(dateFrom) : new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthStr = months[refDate.getMonth()].toUpperCase();

    const parts = raw.split('-');
    if (parts.length === 2 && !isNaN(parseInt(parts[0], 10)) && !isNaN(parseInt(parts[1], 10))) {
      const startDay = String(parseInt(parts[0], 10)).padStart(2, '0');
      const endDay = String(parseInt(parts[1], 10)).padStart(2, '0');
      return `W${idx + 1} (${startDay}-${endDay} ${monthStr})`;
    }

    return `W${idx + 1} (${raw.toUpperCase()})`;
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const str = String(dateStr).split('T')[0].trim();
    if (str.toUpperCase().startsWith('W/O') || str.toUpperCase().startsWith('W')) return str;

    const parts = str.split('-');

    // Case 1: YYYY-MM-DD
    if (parts.length === 3 && parts[0].length === 4) {
      const year = parts[0];
      const monthIdx = parseInt(parts[1], 10) - 1;
      const day = String(parseInt(parts[2], 10)).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${months[monthIdx]} ${year}`;
      }
    }

    // Case 2: Parse standard date, correcting 2-digit/missing year parsing (e.g. "26 JUN" -> 2001)
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      let year = d.getFullYear();
      if (year < 2020) {
        year = dateFrom ? new Date(dateFrom).getFullYear() : new Date().getFullYear();
      }
      const day = String(d.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[d.getMonth()]} ${year}`;
    }
    return dateStr;
  };

  const getProcessedBuckets = () => {
    if (!rawBuckets || rawBuckets.length === 0) {
      return [];
    }

    if (period === 'week') {
      return rawBuckets.map((b, idx) => ({
        ...b,
        formattedLabel: formatWeeklyBucketLabel(b, idx),
        rawIndex: idx
      }));
    }

    if (period === 'month') {
      return rawBuckets.map((b, idx) => ({
        ...b,
        formattedLabel: String(b.label || b.key).toUpperCase(),
        rawIndex: idx
      }));
    }

    // DAY mode:
    return rawBuckets.map((b, idx) => ({
      ...b,
      formattedLabel: formatDateShort(b.label || b.key).toUpperCase(),
      rawIndex: idx
    }));
  };

  const processedBuckets = getProcessedBuckets();

  const getPeriodLabels = () => {
    const today = new Date();

    if (period === 'week') {
      const currMonday = getMondayOf(today);
      const currSunday = new Date(currMonday);
      currSunday.setDate(currMonday.getDate() + 6);

      const prevMonday = new Date(currMonday);
      prevMonday.setDate(currMonday.getDate() - 7);
      const prevSunday = new Date(prevMonday);
      prevSunday.setDate(prevMonday.getDate() + 6);

      const currStart = formatDateShort(currMonday.toISOString().split('T')[0]);
      const currEnd = formatDateShort(currSunday.toISOString().split('T')[0]);

      const prevStart = formatDateShort(prevMonday.toISOString().split('T')[0]);
      const prevEnd = formatDateShort(prevSunday.toISOString().split('T')[0]);

      return {
        currLabel: `${currStart} – ${currEnd}`,
        prevLabel: `${prevStart} – ${prevEnd}`
      };
    }

    if (period === 'month') {
      const currStart = formatDateShort(getNDaysAgoStr(29));
      const currEnd = formatDateShort(today);

      const prevStart = formatDateShort(getNDaysAgoStr(59));
      const prevEnd = formatDateShort(getNDaysAgoStr(30));

      return {
        currLabel: `${currStart} – ${currEnd}`,
        prevLabel: `${prevStart} – ${prevEnd}`
      };
    }

    const currDay = formatDateShort(today);
    const prevDay = formatDateShort(getNDaysAgoStr(1));

    return {
      currLabel: currDay,
      prevLabel: prevDay
    };
  };

  const getDisplayLabels = () => {
    const curr = `${formatDateShort(dateFrom)} – ${formatDateShort(dateTo)}`;
    const prev = comparisonData?.prev_period_label || 'Previous Period';
    return { currLabel: curr, prevLabel: prev };
  };

  const { currLabel, prevLabel } = getDisplayLabels();

  const formatBucketLabel = (label) => {
    if (!label) return '';
    const str = String(label).trim();
    if (period === 'week') {
      if (str.toUpperCase().startsWith('W/O')) return str.toUpperCase();
      const formattedDate = formatDateShort(str);
      if (formattedDate) {
        return `W/O ${formattedDate.toUpperCase()}`;
      }
      return `W/O ${str.toUpperCase()}`;
    }
    return str.toUpperCase();
  };

  const entityA = entities.find(e => String(e.id) === String(entityAId)) || null;
  const entityB = entities.find(e => String(e.id) === String(entityBId)) || null;

  const formatRankStr = (rank) => {
    if (!rank) return 'RANK -';
    const s = ['TH', 'ST', 'ND', 'RD'];
    const v = rank % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `RANK ${rank}${suffix}`;
  };

  const renderRankMove = (move) => {
    if (!move || move === 0) {
      return <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '700' }}>—</span>;
    }
    if (move > 0) {
      return (
        <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
          <FaArrowUp style={{ fontSize: '10px' }} /> {move}
        </span>
      );
    }
    return (
      <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        <FaArrowDown style={{ fontSize: '10px' }} /> {Math.abs(move)}
      </span>
    );
  };

  const getEntityValue = (entity, bucket, index) => {
    if (!entity) return 0;

    if (entity.buckets_data && typeof entity.buckets_data === 'object') {
      if (bucket?.key && bucket.key in entity.buckets_data) {
        const val = Number(entity.buckets_data[bucket.key]);
        if (!isNaN(val)) return val;
      }
      if (bucket?.label && bucket.label in entity.buckets_data) {
        const val = Number(entity.buckets_data[bucket.label]);
        if (!isNaN(val)) return val;
      }
    }

    if (Array.isArray(entity.time_series) && entity.time_series.length > 0) {
      if (index !== undefined && index >= 0 && index < entity.time_series.length) {
        const val = Number(entity.time_series[index]);
        if (!isNaN(val)) return val;
      }
      if (bucket?.rawIndex !== undefined && bucket.rawIndex >= 0 && bucket.rawIndex < entity.time_series.length) {
        const val = Number(entity.time_series[bucket.rawIndex]);
        if (!isNaN(val)) return val;
      }
    }

    return 0;
  };

  const maxBucketCount = React.useMemo(() => {
    let maxVal = 1;
    entities.forEach(e => {
      processedBuckets.forEach((b, idx) => {
        const val = getEntityValue(e, b, idx);
        if (val > maxVal) maxVal = val;
      });
    });
    return maxVal;
  }, [entities, processedBuckets]);

  const maxChartVal = React.useMemo(() => {
    let maxVal = 1;
    processedBuckets.forEach((b, idx) => {
      const valA = getEntityValue(entityA, b, idx);
      const valB = getEntityValue(entityB, b, idx);
      if (valA > maxVal) maxVal = valA;
      if (valB > maxVal) maxVal = valB;
    });
    return maxVal;
  }, [processedBuckets, entityA, entityB]);

  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportExcel = async () => {
    if (exportingExcel || loading || !comparisonData) return;
    setExportingExcel(true);
    try {
      const workbook = XLSX.utils.book_new();

      const activeGranularity = period === 'custom' ? 'day' : period;
      const paramsBase = {
        granularity: activeGranularity,
        period: activeGranularity
      };
      if (dateFrom) paramsBase.date_from = dateFrom;
      if (dateTo) paramsBase.date_to = dateTo;

      // 1. Current mode entities from memory, fetch other mode in background if needed
      let ridersEntities = [];
      let merchantsEntities = [];

      if (mode === 'riders') {
        ridersEntities = entities;
        try {
          const res = await AdminService.getFleetComparison({ ...paramsBase, mode: 'merchants' });
          merchantsEntities = res?.entities || [];
        } catch (e) {
          console.warn('Failed to fetch merchants for report export:', e);
        }
      } else {
        merchantsEntities = entities;
        try {
          const res = await AdminService.getFleetComparison({ ...paramsBase, mode: 'riders' });
          ridersEntities = res?.entities || [];
        } catch (e) {
          console.warn('Failed to fetch riders for report export:', e);
        }
      }

      // 2. Riders Comparison Sheet
      if (ridersEntities.length > 0) {
        const ridersRows = ridersEntities.map(e => {
          const row = {
            'Type': 'Rider',
            'Rider Name': e.name || 'N/A',
            'Current Rank': e.current_rank || 'N/A',
            'Orders (Current Period)': e.current_orders || 0,
            'Total KM': e.total_km || 0
          };
          processedBuckets.forEach((b, idx) => {
            const keyName = b.formattedLabel || b.label || `Bucket ${idx + 1}`;
            row[keyName] = getEntityValue(e, b, idx);
          });
          return row;
        });

        const ridersSheet = XLSX.utils.json_to_sheet(ridersRows);
        XLSX.utils.book_append_sheet(workbook, ridersSheet, 'Riders Comparison');
      }

      // 3. Merchants Comparison Sheet
      if (merchantsEntities.length > 0) {
        const merchantsRows = merchantsEntities.map(e => {
          const row = {
            'Type': 'Merchant',
            'Merchant Name': e.name || 'N/A',
            'Current Rank': e.current_rank || 'N/A',
            'Orders (Current Period)': e.current_orders || 0,
            'Total KM': e.total_km || 0
          };
          processedBuckets.forEach((b, idx) => {
            const keyName = b.formattedLabel || b.label || `Bucket ${idx + 1}`;
            row[keyName] = getEntityValue(e, b, idx);
          });
          return row;
        });

        const merchantsSheet = XLSX.utils.json_to_sheet(merchantsRows);
        XLSX.utils.book_append_sheet(workbook, merchantsSheet, 'Merchants Comparison');
      }

      // 4. Head-to-Head Detail Sheet (if in head-to-head view and entities selected)
      if (view === 'head-to-head' && entityA && entityB) {
        const nameA = entityA.name || 'Entity A';
        const nameB = entityB.name || 'Entity B';

        const h2hRows = [
          {
            'Name': 'Current Rank',
            [nameA]: entityA.current_rank || 'N/A',
            [nameB]: entityB.current_rank || 'N/A'
          },
          {
            'Name': `Orders (${currLabel})`,
            [nameA]: entityA.current_orders || 0,
            [nameB]: entityB.current_orders || 0
          },
          {
            'Name': '% Change',
            [nameA]: `${entityA.pct_change || 0}%`,
            [nameB]: `${entityB.pct_change || 0}%`
          },
          {
            'Name': 'Total Distance (KM)',
            [nameA]: entityA.total_km || 0,
            [nameB]: entityB.total_km || 0
          }
        ];

        processedBuckets.forEach((b, idx) => {
          const keyName = b.formattedLabel || b.label || `Bucket ${idx + 1}`;
          h2hRows.push({
            'Name': keyName,
            [nameA]: getEntityValue(entityA, b, idx),
            [nameB]: getEntityValue(entityB, b, idx)
          });
        });

        const h2hSheet = XLSX.utils.json_to_sheet(h2hRows);
        XLSX.utils.book_append_sheet(workbook, h2hSheet, 'Head to Head Detail');
      }

      const filename = `Fleet_Comparison_Full_Report_${dateFrom || 'start'}_to_${dateTo || 'end'}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Excel Export Error:', err);
      alert(`Failed to export Excel report: ${err.message || 'An error occurred.'}`);
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#F4F4F8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Top View Selector Tabs (Outside Card) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setView('head-to-head')}
            style={{
              padding: '8px 20px',
              fontSize: '12px',
              fontWeight: '800',
              letterSpacing: '0.5px',
              borderRadius: '4px 4px 0 0',
              border: 'none',
              cursor: 'pointer',
              background: view === 'head-to-head' ? '#2A2C41' : '#e2e8f0',
              color: view === 'head-to-head' ? '#ffffff' : '#64748b',
              boxShadow: view === 'head-to-head' ? '0 -2px 5px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            HEAD-TO-HEAD
          </button>
          <button
            onClick={() => {
              setView('period-grid');
              if (period === 'custom') {
                handlePeriodChange('week');
              }
            }}
            style={{
              padding: '8px 20px',
              fontSize: '12px',
              fontWeight: '800',
              letterSpacing: '0.5px',
              borderRadius: '4px 4px 0 0',
              border: 'none',
              cursor: 'pointer',
              background: view === 'period-grid' ? '#2A2C41' : '#e2e8f0',
              color: view === 'period-grid' ? '#ffffff' : '#64748b',
              boxShadow: view === 'period-grid' ? '0 -2px 5px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            PERIOD GRID
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleExportExcel}
            disabled={exportingExcel || loading}
            title="Export Comparison Data to Excel"
            style={{
              background: (exportingExcel || loading) ? '#fdba74' : 'linear-gradient(135deg, #F55D00 0%, #FDBF50 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 14px',
              cursor: (exportingExcel || loading) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '700',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              opacity: (exportingExcel || loading) ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            <FaFileExcel className={exportingExcel ? 'spin' : ''} style={{ fontSize: '13px' }} />
            <span>{exportingExcel ? 'Exporting...' : 'Export Excel'}</span>
          </button>

          <button
            onClick={fetchData}
            disabled={loading || exportingExcel}
            title="Refresh Data"
            style={{
              background: '#2A2C41',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 14px',
              cursor: (loading || exportingExcel) ? 'not-allowed' : 'pointer',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '700',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              opacity: (loading || exportingExcel) ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            <FaSync className={loading ? 'spin' : ''} />
            <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Main Container Card */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

        {/* Unified Sub-Header Controls */}
        <div style={{ padding: '16px 24px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '800', letterSpacing: '0.5px', color: '#2A2C41' }}>
              COMPARISON
            </span>

            {/* Mode Selectors: Riders vs Merchants */}
            <div style={{ display: 'flex', background: '#2A2C41', borderRadius: '4px', padding: '2px' }}>
              <button
                onClick={() => setMode('riders')}
                style={{
                  padding: '6px 14px',
                  fontSize: '11px',
                  fontWeight: '700',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  background: mode === 'riders' ? '#F55D00' : 'transparent',
                  color: '#ffffff',
                  transition: 'all 0.2s'
                }}
              >
                RIDERS
              </button>
              <button
                onClick={() => setMode('merchants')}
                style={{
                  padding: '6px 14px',
                  fontSize: '11px',
                  fontWeight: '700',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  background: mode === 'merchants' ? '#F55D00' : 'transparent',
                  color: '#ffffff',
                  transition: 'all 0.2s'
                }}
              >
                MERCHANTS
              </button>
            </div>

            {/* Date Preset Segmented Control (DAY | WEEK | MONTH) */}
            <div style={{ display: 'inline-flex', background: '#e2e8f0', borderRadius: '6px', padding: '3px', alignItems: 'center', gap: '2px' }}>
              <button
                type="button"
                onClick={() => handlePeriodChange('day')}
                style={{
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: period === 'day' ? '800' : '700',
                  letterSpacing: '0.5px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: period === 'day' ? '#F55D00' : 'transparent',
                  color: period === 'day' ? '#ffffff' : '#2A2C41',
                  boxShadow: period === 'day' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                DAY
              </button>
              <button
                type="button"
                onClick={() => handlePeriodChange('week')}
                style={{
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: period === 'week' ? '800' : '700',
                  letterSpacing: '0.5px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: period === 'week' ? '#F55D00' : 'transparent',
                  color: period === 'week' ? '#ffffff' : '#2A2C41',
                  boxShadow: period === 'week' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                WEEK
              </button>
              <button
                type="button"
                onClick={() => handlePeriodChange('month')}
                style={{
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: period === 'month' ? '800' : '700',
                  letterSpacing: '0.5px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: period === 'month' ? '#F55D00' : 'transparent',
                  color: period === 'month' ? '#ffffff' : '#2A2C41',
                  boxShadow: period === 'month' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                MONTH
              </button>
              {view === 'head-to-head' && (
                <button
                  type="button"
                  onClick={() => setPeriod('custom')}
                  style={{
                    padding: '6px 16px',
                    fontSize: '12px',
                    fontWeight: period === 'custom' ? '800' : '700',
                    letterSpacing: '0.5px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    background: period === 'custom' ? '#F55D00' : 'transparent',
                    color: period === 'custom' ? '#ffffff' : '#2A2C41',
                    boxShadow: period === 'custom' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  CUSTOM
                </button>
              )}
            </div>

            {/* Custom Date Range Pickers (Appears in Head-to-Head view when CUSTOM is clicked) */}
            {view === 'head-to-head' && period === 'custom' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 10px' }}>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => {
                    if (e.target.value) {
                      setDateFrom(e.target.value);
                    }
                  }}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', fontWeight: '700', color: '#1e293b', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>to</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => {
                    if (e.target.value) {
                      setDateTo(e.target.value);
                    }
                  }}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', fontWeight: '700', color: '#1e293b', cursor: 'pointer' }}
                />
              </div>
            )}
          </div>

          <div style={{ fontSize: '13px', fontWeight: '700', color: '#64748b' }}>
            {currLabel}
          </div>
        </div>

        {/* Loading / Error Overlay */}
        {loading && (
          <div style={{ padding: '60px', textAlign: 'center', color: '#3B6B9C', fontWeight: '600' }}>
            <FaSync className="spin" style={{ fontSize: '24px', marginBottom: '12px' }} />
            <div>Loading comparison analytics for selected date range...</div>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444', fontWeight: '600' }}>
            {error}
          </div>
        )}

        {!loading && !error && (!comparisonData || entities.length === 0) && (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: '600' }}>
            No comparison data available for the selected period.
          </div>
        )}

        {!loading && !error && comparisonData && entities.length > 0 && (
          <>
            {/* VIEW 1: HEAD-TO-HEAD */}
            {view === 'head-to-head' && (
              <div style={{ padding: '24px' }}>

                {/* Entity Comparison Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px' }}>

                  {/* Entity A Card */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '12px', height: '12px', backgroundColor: '#F55D00', borderRadius: '2px', display: 'inline-block' }}></span>
                        <select
                          value={entityAId}
                          onChange={(e) => {
                            const newA = e.target.value;
                            if (newA && String(newA) === String(entityBId)) {
                              setEntityBId(entityAId);
                            }
                            setEntityAId(newA);
                          }}
                          style={{ fontSize: '15px', fontWeight: '700', padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', color: entityAId ? '#2A2C41' : '#64748b', minWidth: '180px' }}
                        >
                          <option value="" disabled>
                            Select {mode === 'riders' ? 'Rider' : 'Merchant'}
                          </option>
                          {entities.map(e => {
                            const isSelectedInOtherCard = entityBId && String(e.id) === String(entityBId);
                            return (
                              <option key={e.id} value={e.id} disabled={isSelectedInOtherCard}>
                                {e.name} {isSelectedInOtherCard ? '(Already Selected)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px' }}>
                        {formatRankStr(entityA?.current_rank)}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontSize: '36px', fontWeight: '800', color: '#2A2C41', lineHeight: 1 }}>
                            {entityA?.current_orders || 0}
                          </span>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            orders in {currLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginTop: '6px' }}>
                          Total Distance: <span style={{ color: '#2A2C41', fontWeight: '800' }}>{entityA?.total_km || 0} km</span>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '800', color: (entityA?.pct_change || 0) >= 0 ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {entityA?.pct_change || 0}% {(entityA?.pct_change || 0) >= 0 ? '▲' : '▼'}
                      </div>
                    </div>
                  </div>

                  {/* Entity B Card */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '12px', height: '12px', backgroundColor: '#FDBF50', border: '1px solid #f59e0b', borderRadius: '2px', display: 'inline-block' }}></span>
                        <select
                          value={entityBId}
                          onChange={(e) => {
                            const newB = e.target.value;
                            if (newB && String(newB) === String(entityAId)) {
                              setEntityAId(entityBId);
                            }
                            setEntityBId(newB);
                          }}
                          style={{ fontSize: '15px', fontWeight: '700', padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', color: entityBId ? '#2A2C41' : '#64748b', minWidth: '180px' }}
                        >
                          <option value="" disabled>
                            Select {mode === 'riders' ? 'Rider' : 'Merchant'}
                          </option>
                          {entities.map(e => {
                            const isSelectedInOtherCard = entityAId && String(e.id) === String(entityAId);
                            return (
                              <option key={e.id} value={e.id} disabled={isSelectedInOtherCard}>
                                {e.name} {isSelectedInOtherCard ? '(Already selected)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '0.5px' }}>
                        {formatRankStr(entityB?.current_rank)}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontSize: '36px', fontWeight: '800', color: '#2A2C41', lineHeight: 1 }}>
                            {entityB?.current_orders || 0}
                          </span>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            orders in {currLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginTop: '6px' }}>
                          Total Distance: <span style={{ color: '#2A2C41', fontWeight: '800' }}>{entityB?.total_km || 0} km</span>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '800', color: (entityB?.pct_change || 0) >= 0 ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {entityB?.pct_change || 0}% {(entityB?.pct_change || 0) >= 0 ? '▲' : '▼'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle Section: Chart */}
                <div style={{ marginBottom: '24px' }}>

                  {/* Paired Bar Chart */}
                  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#2A2C41', margin: 0 }}>
                        Orders by period • {currLabel}
                      </h4>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>
                        <span style={{ color: '#F55D00', fontWeight: '800' }}>Orange</span> – {entityA?.name || (mode === 'riders' ? 'Select Rider' : 'Select Merchant')} • <span style={{ color: '#d97706', fontWeight: '800' }}>Yellow</span> – {entityB?.name || (mode === 'riders' ? 'Select Rider' : 'Select Merchant')}
                      </span>
                    </div>

                    {/* Adaptive Chart Container */}
                    {(() => {
                      const isCustomScroll = processedBuckets.length > 12;
                      return (
                        <div style={{ overflowX: isCustomScroll ? 'auto' : 'visible', paddingBottom: '8px' }}>
                          <div style={{ minWidth: isCustomScroll ? `${processedBuckets.length * 48}px` : '100%', width: '100%' }}>

                            {/* Row 1: Fixed Height Bars Area */}
                            <div style={{ height: '180px', display: 'flex', alignItems: 'flex-end', gap: isCustomScroll ? '12px' : '16px', borderBottom: '2px solid #cbd5e1' }}>
                              {processedBuckets.map((b, idx) => {
                                const valA = getEntityValue(entityA, b, idx);
                                const valB = getEntityValue(entityB, b, idx);

                                const heightA = roundPx(valA, maxChartVal, 130);
                                const heightB = roundPx(valB, maxChartVal, 130);

                                return (
                                  <div key={b.key || idx} style={{ flex: 1, minWidth: isCustomScroll ? '40px' : 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', width: '100%', justifyContent: 'center' }}>

                                      {/* Bar A */}
                                      <div style={{ width: isCustomScroll ? '18px' : '45%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ fontSize: '10px', fontWeight: '800', color: '#F55D00', marginBottom: '2px' }}>{valA}</span>
                                        <div style={{ width: '100%', height: `${heightA}px`, backgroundColor: '#F55D00', borderRadius: '2px 2px 0 0', transition: 'height 0.3s ease' }}></div>
                                      </div>

                                      {/* Bar B */}
                                      <div style={{ width: isCustomScroll ? '18px' : '45%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ fontSize: '10px', fontWeight: '800', color: '#d97706', marginBottom: '2px' }}>{valB}</span>
                                        <div style={{ width: '100%', height: `${heightB}px`, backgroundColor: '#FDBF50', border: '1px solid #f59e0b', borderRadius: '2px 2px 0 0', transition: 'height 0.3s ease' }}></div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Row 2: Fixed X-Axis Date Labels Row */}
                            <div style={{ display: 'flex', gap: isCustomScroll ? '12px' : '16px', marginTop: '8px' }}>
                              {processedBuckets.map((b, idx) => {
                                const labelText = String(b.formattedLabel || b.label || '');
                                const parts = labelText.split(' ');

                                return (
                                  <div key={b.key || idx} style={{ flex: 1, minWidth: isCustomScroll ? '40px' : 0, textAlign: 'center' }}>
                                    {parts.length >= 3 ? (
                                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', lineHeight: '1.2' }}>
                                        <div>{parts[0]} {parts[1]}</div>
                                        <div style={{ fontSize: '9px', color: '#94a3b8' }}>{parts[2]}</div>
                                      </div>
                                    ) : (
                                      <span style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', display: 'block', wordBreak: 'break-word', lineHeight: '1.2' }}>
                                        {labelText}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* VIEW 2: PERIOD GRID */}
            {view === 'period-grid' && (
              <div>

                {/* Dark Header Strip */}
                <div style={{ background: '#2A2C41', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#ffffff' }}>
                  <div style={{ fontSize: '14px', fontWeight: '800', textTransform: 'capitalize' }}>
                    {mode} <span style={{ fontSize: '12px', fontWeight: '400', color: '#94a3b8', marginLeft: '8px' }}>{currLabel} vs {prevLabel}</span>
                  </div>
                </div>

                {/* Heat-map Matrix Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#64748b' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '800', width: '200px' }}>
                          {mode === 'riders' ? 'RIDER' : 'MERCHANT'}
                        </th>
                        {processedBuckets.map(b => (
                          <th key={b.key} style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '800' }}>
                            {b.formattedLabel || b.label}
                          </th>
                        ))}
                        <th style={{ padding: '12px 12px', textAlign: 'center', fontWeight: '800', background: '#fff7ed', color: '#c2410c' }}>TREND</th>
                        <th style={{ padding: '12px 12px', textAlign: 'center', fontWeight: '800', background: '#fff7ed', color: '#c2410c' }}>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entities.map(entity => {
                        const rowTotal = processedBuckets.reduce((sum, b, idx) => sum + getEntityValue(entity, b, idx), 0);

                        return (
                          <tr key={entity.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '12px 16px', fontWeight: '700', color: '#2A2C41' }}>
                              {entity.name}
                            </td>

                            {/* Bucket Heat-map Cells */}
                            {processedBuckets.map((b, bIdx) => {
                              const val = getEntityValue(entity, b, bIdx);
                              const opacity = maxBucketCount > 0 ? Math.min(val / maxBucketCount, 1) : 0;
                              const bgColor = val > 0 ? `rgba(245, 93, 0, ${0.1 + opacity * 0.75})` : '#ffffff';
                              const textColor = opacity > 0.5 ? '#ffffff' : (val > 0 ? '#2A2C41' : '#cbd5e1');

                              return (
                                <td
                                  key={b.key || bIdx}
                                  style={{
                                    padding: '12px 8px',
                                    textAlign: 'center',
                                    fontWeight: '800',
                                    backgroundColor: bgColor,
                                    color: textColor,
                                    borderRight: '1px solid #f1f5f9'
                                  }}
                                >
                                  {val}
                                </td>
                              );
                            })}

                            {/* Sparkbar Trend Column */}
                            <td style={{ padding: '8px 12px', textAlign: 'center', background: '#f8fafc', borderLeft: '1px solid #cbd5e1' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px', height: '24px' }}>
                                {processedBuckets.map((b, idx) => {
                                  const val = getEntityValue(entity, b, idx);
                                  const maxVal = Math.max(...processedBuckets.map((b_i, i) => getEntityValue(entity, b_i, i)), 1);
                                  const h = Math.max((val / maxVal) * 20, val > 0 ? 4 : 1);
                                  return (
                                    <div
                                      key={b.key || idx}
                                      style={{
                                        width: '4px',
                                        height: `${h}px`,
                                        backgroundColor: val > 0 ? '#F55D00' : '#cbd5e1',
                                        borderRadius: '1px'
                                      }}
                                    ></div>
                                  );
                                })}
                              </div>
                            </td>

                            {/* Total Row Column */}
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: '800', color: '#c2410c', background: '#fff7ed', borderLeft: '1px solid #ffedd5' }}>
                              {rowTotal}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer Insight Note */}
                <div style={{ padding: '14px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#94a3b8' }}>
                  Cell shade is share of the busiest cell on the sheet, so a cold row reads as a cold row across the whole period. Switch to {mode === 'riders' ? 'Merchants' : 'Riders'} and the dormant accounts show as an unbroken empty band — the fastest possible read of a dead account.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

function roundPx(val, maxVal, maxHeight = 140) {
  if (!maxVal || maxVal <= 0 || !val || val <= 0) return 2;
  const px = Math.round((val / maxVal) * maxHeight);
  return Math.min(Math.max(px, val > 0 ? 8 : 2), maxHeight);
}

export default FleetComparison;
